import React, { useState, useEffect } from 'react';
import { auditLogApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatTimestamp } from '../../utils/dateUtils';

const AuditLogList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { hasPermission } = useAuth();
  
  // Filter states
  const [userFilter, setUserFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        
        let response;
        
        // Apply filters if provided
        if (userFilter) {
          response = await auditLogApi.getByUser(userFilter);
        } else if (resourceFilter) {
          response = await auditLogApi.getByResource(resourceFilter);
        } else if (actionFilter) {
          response = await auditLogApi.getByAction(actionFilter);
        } else {
          response = await auditLogApi.getAll();
        }
        
        // Sort logs with newest first
        const sortedLogs = response.data.sort((a, b) => {
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
        
        setLogs(sortedLogs);
        setError(null);
      } catch (err) {
        console.error('Error fetching audit logs:', err);
        setError('Failed to load audit logs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [userFilter, resourceFilter, actionFilter]);
  
  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all audit logs? This action cannot be undone.')) {
      return;
    }
    
    try {
      await auditLogApi.clear();
      
      // Clear logs in state
      setLogs([]);
      
      // Show success message
      alert('Audit logs cleared successfully.');
    } catch (err) {
      console.error('Error clearing audit logs:', err);
      alert(err.response?.data?.detail || 'Failed to clear audit logs. Please try again.');
    }
  };
  
  
  // Get action badge class
  const getActionBadgeClass = (action) => {
    const classMap = {
      'create': 'bg-success',
      'update': 'bg-primary',
      'delete': 'bg-danger',
      'view': 'bg-info',
      'login': 'bg-warning'
    };
    
    return classMap[action] || 'bg-secondary';
  };
  
  if (!hasPermission('audit_logs', 'view')) {
    return (
      <div className="alert alert-danger" role="alert">
        You don't have permission to view audit logs.
      </div>
    );
  }
  
  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Audit Logs</h1>
        
        {hasPermission('audit_logs', 'delete') && (
          <button 
            className="btn btn-danger"
            onClick={handleClearLogs}
            disabled={loading || logs.length === 0}
          >
            <i className="fas fa-trash me-2"></i>
            Clear All Logs
          </button>
        )}
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="card shadow mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0">Filters</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="resourceFilter" className="form-label">Resource Type</label>
              <select
                className="form-select"
                id="resourceFilter"
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
              >
                <option value="">All Resources</option>
                <option value="members">Members</option>
                <option value="payments">Payments</option>
                <option value="expenses">Expenses</option>
                <option value="expense_payments">Expense Payments</option>
                <option value="users">Users</option>
                <option value="auth">Authentication</option>
              </select>
            </div>
            
            <div className="col-md-4">
              <label htmlFor="actionFilter" className="form-label">Action</label>
              <select
                className="form-select"
                id="actionFilter"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="view">View</option>
                <option value="login">Login</option>
              </select>
            </div>
            
            <div className="col-md-4 d-flex align-items-end">
              <button
                className="btn btn-secondary w-100"
                onClick={() => {
                  setResourceFilter('');
                  setActionFilter('');
                  setUserFilter('');
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="card shadow">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Resource</th>
                    <th>Resource ID</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        No audit logs found.
                      </td>
                    </tr>
                  ) : (
                    logs.map(log => (
                      <tr key={log.id}>
                        <td>{formatTimestamp(log.timestamp)}</td>
                        <td>
                          <span 
                            className="cursor-pointer text-primary"
                            onClick={() => setUserFilter(log.user_id)}
                            title="Filter by this user"
                          >
                            {log.username}
                          </span>
                        </td>
                        <td>
                          <span 
                            className={`badge ${getActionBadgeClass(log.action)}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setActionFilter(log.action)}
                            title="Filter by this action"
                          >
                            {log.action}
                          </span>
                        </td>
                        <td>
                          <span 
                            className="cursor-pointer text-primary"
                            onClick={() => setResourceFilter(log.resource_type)}
                            title="Filter by this resource"
                          >
                            {log.resource_type}
                          </span>
                        </td>
                        <td>{log.resource_id || '-'}</td>
                        <td>
                          {log.details ? (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                // Create a more readable details message
                                const detailsStr = typeof log.details === 'object' 
                                  ? Object.entries(log.details)
                                      .map(([key, value]) => `${key}: ${value}`)
                                      .join('\n')
                                  : String(log.details);
                                
                                alert(`Additional Information for ${log.action} on ${log.resource_type}:\n\n${detailsStr}`);
                              }}
                              title="View additional information about this action"
                            >
                              <i className="fas fa-info-circle"></i>
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogList;
